import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { visuallyHidden } from "@mui/utils";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../../../Contexts/AppContext";
import { DeviceHistoryTableProps } from "./types";
import DeviceHistoryApi from "../../../../api/device-history";
import { DeviceHistory } from "../../../../models/DeviceHistory";
import { DatesUtils } from "../../../../utils/DatesUtils";
import { Device } from "../../../../models/Device";
import { SortOrder } from "mongoose";
import { Button, CircularProgress } from "@mui/material";
import DateRangesFilter from "./DateRangesFilter/DateRangesFilter";
import { Dayjs } from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
	order: Order,
	orderBy: Key
): (
	a: { [key in Key]: number | string },
	b: { [key in Key]: number | string }
) => number {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
	disablePadding: boolean;
	id: keyof DeviceHistory;
	label: string;
	numeric: boolean;
}

const headCells: readonly HeadCell[] = [
	{
		id: "timestamp",
		numeric: true,
		disablePadding: false,
		label: "Fecha de adquisicion",
	},
	{
		id: "deviceReport",
		numeric: true,
		disablePadding: false,
		label: "Dispositivo",
	},
];

interface EnhancedTableProps {
	numSelected: number;
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof DeviceHistory
	) => void;
	onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
	order: Order;
	orderBy: string;
	rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
	const {
		onSelectAllClick,
		order,
		orderBy,
		numSelected,
		rowCount,
		onRequestSort,
	} = props;
	const createSortHandler =
		(property: keyof DeviceHistory) => (event: React.MouseEvent<unknown>) => {
			onRequestSort(event, property);
		};

	return (
		<TableHead>
			<TableRow>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={"center"}
						padding={headCell.disablePadding ? "none" : "normal"}
						sortDirection={orderBy === headCell.id ? order : false}>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : "asc"}
							onClick={createSortHandler(headCell.id)}>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component='span' sx={visuallyHidden}>
									{order === "desc" ? "sorted descending" : "sorted ascending"}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}
interface EnhancedTableToolbarProps {
	numSelected: number;
	device: Device;
}
function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
	const { numSelected, device } = props;
	return (
		<Toolbar
			sx={[
				{
					pl: { sm: 2 },
					pr: { xs: 1, sm: 1 },
				},
				numSelected > 0 && {
					bgcolor: (theme) =>
						alpha(
							theme.palette.primary.main,
							theme.palette.action.activatedOpacity
						),
				},
			]}>
			{numSelected > 0 ? (
				<Typography
					sx={{ flex: "1 1 100%" }}
					color='inherit'
					variant='subtitle1'
					component='div'>
					{numSelected} selected
				</Typography>
			) : (
				<Typography
					sx={{ flex: "1 1 100%" }}
					variant='h6'
					id='tableTitle'
					component='div'>
					Historial {device.deviceId}
				</Typography>
			)}
			{numSelected > 0 ? (
				<Tooltip title='Delete'>
					<IconButton>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			) : (
				<Tooltip title='Filter list'>
					<IconButton>
						<FilterListIcon />
					</IconButton>
				</Tooltip>
			)}
		</Toolbar>
	);
}
export default function DeviceHistoryTable(props: DeviceHistoryTableProps) {
	const { device } = props;
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [rows, setRows] = useState<DeviceHistory[]>([]);
	const [order, setOrder] = useState<Order>("desc");
	const [orderBy, setOrderBy] = useState<keyof DeviceHistory>("timestamp");
	const [selected, setSelected] = useState<readonly number[]>([]);
	const [page, setPage] = useState(0);
	const [dense, setDense] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);

	const { userContext } = useContext(AppContext);

	const fetchHistory = useCallback(async () => {
		if (!device || !userContext?.permissionaryId || !startDate || !endDate)
			return;
		const limit = rowsPerPage + 1;
		const offset = page * rowsPerPage;
		const sort: Record<string, SortOrder> = {
			[orderBy]: order === "asc" ? 1 : -1,
		};

		const startDateDto = startDate.startOf("day").toDate().getTime();
		const endDateDto = endDate.endOf("day").toDate().getTime();

		try {
			setLoading(true);

			const response = await DeviceHistoryApi.getDeviceHistory({
				accessToken: localStorage.getItem("accessToken") || "",
				searchDto: {
					query: {
						deviceId: device.deviceId,
						permissionaryId: userContext.permissionaryId,
					},
					sort,
					offset,
					limit,
					timestampRange: {
						from: startDateDto,
						end: endDateDto,
					},
				},
			});

			setRows(response);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [
		device,
		userContext,
		order,
		rowsPerPage,
		page,
		orderBy,
		startDate,
		endDate,
	]);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof DeviceHistory
	) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
		const selectedIndex = selected.indexOf(id);
		let newSelected: readonly number[] = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selected, id);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selected.slice(1));
		} else if (selectedIndex === selected.length - 1) {
			newSelected = newSelected.concat(selected.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selected.slice(0, selectedIndex),
				selected.slice(selectedIndex + 1)
			);
		}
		setSelected(newSelected);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows =
		page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

	const visibleRows = useMemo(() => {
		return (
			[...rows]
				//@ts-expect-error - TS complains about the sort method, but it is a valid method for arrays
				.sort(getComparator(order, orderBy))
				.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
		);
	}, [order, orderBy, page, rowsPerPage, rows]);

	const onDateRangeChange = useCallback(
		(date: Dayjs | null, prop: "startDate" | "endDate") => {
			if (prop === "startDate") {
				setStartDate(date);
			} else if (prop === "endDate") {
				setEndDate(date);
			}
		},
		[]
	);

	const handleRefresh = useCallback(() => {
		const shouldFetchNow = page === 0 && rowsPerPage === 5;
		setPage(0);
		setRowsPerPage(5);
		setSelected([]);
		if (shouldFetchNow) {
			fetchHistory();
		}
	}, [fetchHistory, page, rowsPerPage]);

	useEffect(() => {
		fetchHistory();
	}, [fetchHistory]);

	return (
		<Box sx={{ width: "100%" }}>
			<Paper
				elevation={0}
				sx={{
					p: 2,
					mb: 2,
					borderRadius: 3,
					border: "1px solid",
					borderColor: "divider",
					background:
						"linear-gradient(180deg, rgba(250,250,252,1) 0%, rgba(255,255,255,1) 60%)",
				}}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
						flexWrap: "wrap",
						justifyContent: "space-between",
					}}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
						<IconButton
							color='primary'
							aria-label='volver'
							onClick={() => {
								navigate("/devices/" + device.id);
							}}
							sx={{
								backgroundColor: (theme) =>
									alpha(theme.palette.primary.main, 0.08),
								border: "1px solid",
								borderColor: "primary.light",
							}}>
							<ArrowBackIcon />
						</IconButton>
						<Box>
							<Typography variant='h6'>Historial del dispositivo</Typography>
							<Typography variant='body2' color='text.secondary'>
								ID: {device.deviceId}
							</Typography>
						</Box>
					</Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							flexWrap: "wrap",
							justifyContent: "flex-end",
						}}>
						<Button
							variant='outlined'
							startIcon={<RefreshIcon />}
							onClick={handleRefresh}
							disabled={loading}>
							Actualizar
						</Button>
						<Box sx={{ minWidth: 260 }}>
							<DateRangesFilter
								onDateChange={(
									date: Dayjs | null,
									prop: "startDate" | "endDate"
								) => onDateRangeChange(date, prop)}
							/>
						</Box>
					</Box>
				</Box>
			</Paper>

			{loading && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						py: 6,
					}}>
					<CircularProgress size={64} />
				</Box>
			)}
			{!loading && (
				<Paper
					sx={{
						width: "100%",
						mb: 2,
						borderRadius: 3,
						border: "1px solid",
						borderColor: "divider",
						overflow: "hidden",
					}}>
					<EnhancedTableToolbar numSelected={selected.length} device={device} />
					<TableContainer
						sx={{
							backgroundColor: "background.paper",
						}}>
						<Table
							sx={{ minWidth: 750 }}
							aria-labelledby='tableTitle'
							size={dense ? "small" : "medium"}>
							<EnhancedTableHead
								numSelected={selected.length}
								order={order}
								orderBy={orderBy}
								onSelectAllClick={() => 0}
								onRequestSort={handleRequestSort}
								rowCount={rows.length}
							/>
							<TableBody>
								{rows.length === 0 && (
									<TableRow>
										<TableCell colSpan={6} align='center'>
											<Typography variant='body1' color='text.secondary'>
												No hay registros en este rango de fechas.
											</Typography>
										</TableCell>
									</TableRow>
								)}
								{visibleRows.map((row) => {
									return (
										<TableRow
											hover
											// onClick={}
											role='checkbox'
											// aria-checked={isItemSelected}
											tabIndex={-1}
											key={row._id}
											// selected={isItemSelected}
											sx={{
												cursor: "pointer",
												"&:nth-of-type(odd)": {
													backgroundColor: (theme) =>
														alpha(theme.palette.primary.main, 0.03),
												},
											}}>
											<TableCell align='center'>
												<Typography variant='body1'>
													{DatesUtils.convertTimeStampToLocalDateTimeString(
														row.timestamp
													)}
												</Typography>
											</TableCell>

											<TableCell align='center'>
												<Chip
													label={
														row.deviceReport.online === true
															? "En linea"
															: "Fuera de linea"
													}
													size='small'
													sx={{
														px: 1,
														fontWeight: 600,
														color: "common.white",
														backgroundColor:
															row.deviceReport.online === true
																? "success.main"
																: "error.main",
														boxShadow: (theme) =>
															`0 6px 16px ${alpha(
																row.deviceReport.online === true
																	? theme.palette.success.main
																	: theme.palette.error.main,
																0.25
															)}`,
													}}
												/>
											</TableCell>
										</TableRow>
									);
								})}
								{emptyRows > 0 && (
									<TableRow
										style={{
											height: (dense ? 33 : 53) * emptyRows,
										}}>
										<TableCell colSpan={6} />
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000, 5000]}
						component='div'
						count={rows.length}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Paper>
			)}
		</Box>
	);
}
